import type { IScreenShareVoiceState, VoiceMicState } from '@srm/screen-sharing/light';
import { createValueStore, sameItems, type IValueStore } from '@srm/ui/lib/valueStore';

/**
 * Голос показа (IMPLEMENTATION.md §31): микрофон пользователя (по умолчанию выключен), у кого из специалистов он
 * включён и кто говорит — по `participantKey` (у специалиста — его `peerId`).
 */
export type ISupportVoiceState = IScreenShareVoiceState;
export type SupportMicState = VoiceMicState;

/** До первого состояния клиента: голос доступен, микрофон выключен. */
export const initialSupportVoice: ISupportVoiceState = {
    available: true,
    mic: 'off',
    active: [],
    speaking: [],
    playbackBlocked: false,
};

export const SUPPORT_MIC_DENIED_HINT =
    'Браузер запретил микрофон. Разрешите его в настройках сайта — значок слева от адреса — и нажмите «Микрофон» ещё раз';
export const SUPPORT_MIC_NO_DEVICE_HINT = 'Подключите микрофон и нажмите «Микрофон» ещё раз';
export const SUPPORT_MIC_ERROR_HINT =
    'Возможно, микрофон занят другой программой. Закройте её и нажмите «Микрофон» ещё раз';

/** Подсказка к отказу микрофона: что случилось (текст клиента) и что сделать. */
export function micFailureHint(voice: Pick<ISupportVoiceState, 'mic' | 'micError'>): string | null {
    if (voice.mic === 'denied') return SUPPORT_MIC_DENIED_HINT;
    if (voice.mic === 'no-device') return [voice.micError, SUPPORT_MIC_NO_DEVICE_HINT].filter(Boolean).join('. ');
    if (voice.mic === 'error') return [voice.micError, SUPPORT_MIC_ERROR_HINT].filter(Boolean).join('. ');
    return null;
}

/**
 * Уровень своего микрофона (0…1) — четыре раза в секунду, пока он включён: мимо состояния показа и контекста, чтобы
 * не перерисовывать полосу целиком; читает только индикатор на кнопке «Микрофон».
 */
export type ISupportMicLevel = IValueStore<number>;

export const createMicLevel = (initial = 0): ISupportMicLevel => createValueStore(initial);

/**
 * Кто говорит (`participantKey`), несколько раз в секунду во время разговора: мимо состояния показа и контекста, как
 * уровень микрофона, — иначе на каждую смену перерисовывались бы шапка, меню и чат. Читают только кружки и подпись
 * «говорит» полосы.
 */
export type ISupportSpeaking = IValueStore<readonly string[]>;

export const createSpeaking = (initial: readonly string[] = []): ISupportSpeaking =>
    createValueStore<readonly string[]>(initial, sameItems);
